import { useEffect, useMemo, useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogDescription,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Minus, Plus, Info, Leaf } from "lucide-react";
import { useCart } from "@/context/CartContext";
import { api, money } from "@/lib/api";
import { formatKcal } from "@/lib/dietary";
import { DietaryBadges } from "@/components/DietaryBadges";
import NutritionModal from "@/components/NutritionModal";
import { toast } from "sonner";

export default function AddonDialog({ item, onClose }) {
  const { addLine } = useCart();
  const [qty, setQty] = useState(1);
  const [selections, setSelections] = useState({});
  const [nutritionOpen, setNutritionOpen] = useState(false);
  const [notes, setNotes] = useState("");
  const [priceColumn, setPriceColumn] = useState("");
  // ── Phase 25.18 Inventory link — fetch the "Made with" ingredient list
  // (hidden chef-secret rows are filtered server-side). Quietly fails if
  // the menu item has no recipe attached.
  const [madeWith, setMadeWith] = useState([]);
  useEffect(() => {
    if (!item?.id) { setMadeWith([]); return; }
    let cancelled = false;
    api.get(`/menu/${item.id}/recipe-public`)
      .then((r) => { if (!cancelled) setMadeWith(r.data?.made_with || []); })
      .catch(() => { if (!cancelled) setMadeWith([]); });
    return () => { cancelled = true; };
  }, [item?.id]);

  // Per-category pricing layout (passed via item._category when opened from MenuPage)
  const category = item?._category;
  const priceCols = category?.price_columns?.length
    ? category.price_columns
    : [{ key: "default", label: "" }];
  const isWhole = category?.price_format === "whole";

  useEffect(() => {
    if (!item) return;
    // Default to the first column that has a value
    const firstWithValue = priceCols.find((c) => {
      const v = (item.prices || {})[c.key];
      return v != null && v !== "" && Number(v) > 0;
    });
    setPriceColumn(firstWithValue ? firstWithValue.key : priceCols[0]?.key || "default");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [item]);

  const unitPrice = useMemo(() => {
    if (!item) return 0;
    const fromCol = (item.prices || {})[priceColumn];
    if (fromCol != null && fromCol !== "") return Number(fromCol);
    return Number(item.price) || 0;
  }, [item, priceColumn]);

  useEffect(() => {
    if (!item) return;
    const init = {};
    (item.addons || []).forEach((g, i) => {
      if (g.type === "single" && g.required && (g.options || []).length > 0) {
        init[i] = g.options[0].name;
      } else if (g.type === "single") {
        init[i] = "";
      } else {
        init[i] = [];
      }
    });
    setSelections(init);
    setQty(1);
    setNotes("");
  }, [item]);

  const flatSelections = useMemo(() => {
    if (!item) return [];
    const out = [];
    (item.addons || []).forEach((g, i) => {
      const val = selections[i];
      if (g.type === "single") {
        const opt = (g.options || []).find((o) => o.name === val);
        if (opt) out.push(opt);
      } else {
        (val || []).forEach((name) => {
          const opt = (g.options || []).find((o) => o.name === name);
          if (opt) out.push(opt);
        });
      }
    });
    return out;
  }, [item, selections]);

  const lineTotalPreview = useMemo(() => {
    if (!item) return 0;
    const addonsSum = flatSelections.reduce((s, a) => s + (Number(a.price) || 0), 0);
    return (unitPrice + addonsSum) * qty;
  }, [item, flatSelections, qty, unitPrice]);

  if (!item) return null;

  function toggleMulti(groupIndex, optionName) {
    setSelections((prev) => {
      const arr = new Set(prev[groupIndex] || []);
      if (arr.has(optionName)) arr.delete(optionName);
      else arr.add(optionName);
      return { ...prev, [groupIndex]: Array.from(arr) };
    });
  }

  function setSingle(groupIndex, optionName) {
    setSelections((prev) => ({ ...prev, [groupIndex]: optionName }));
  }

  function handleAdd() {
    const missing = (item.addons || []).find((g, i) => {
      if (g.required && g.type === "single") return !selections[i];
      return false;
    });
    if (missing) {
      toast.error(`Please choose: ${missing.name}`);
      return;
    }
    addLine({
      item_id: item.id,
      name: item.name,
      base_price: unitPrice,
      quantity: qty,
      selected_addons: flatSelections,
      notes,
      calories: Number(item.calories) || 0,
      tags: item.tags || [],
      price_column: priceColumn,
      price_label: priceCols.find((c) => c.key === priceColumn)?.label || "",
    });
    toast.success(`${item.name} added to your order`);
    onClose();
  }

  return (
    <Dialog open={!!item} onOpenChange={(o) => !o && onClose()}>
      <DialogContent
        data-testid="addon-dialog"
        className="max-w-2xl max-h-[92vh] overflow-y-auto p-0 rounded-2xl"
      >
        <div className="relative h-56 md:h-64 overflow-hidden rounded-t-2xl">
          <img src={item.image_url} alt={item.name} className="w-full h-full object-cover" />
        </div>

        <div className="p-6 md:p-8">
          <DialogHeader>
            <DialogTitle className="font-serif-display text-3xl md:text-4xl text-[#121614]">
              {item.name}
            </DialogTitle>
            {item.origin && (
              <p className="text-xs uppercase tracking-[0.15em] font-bold text-[#5A7247]">
                {item.origin}
              </p>
            )}
            <DialogDescription className="text-[#5C625E] leading-relaxed pt-1">
              {item.description}
            </DialogDescription>
          </DialogHeader>

          <div className="mt-4 flex items-center justify-between gap-3 flex-wrap">
            <DietaryBadges tags={item.tags} testIdPrefix="dialog-diet" />
            <div className="flex items-center gap-3">
              {item.calories > 0 && (
                <p
                  data-testid="dialog-calories"
                  className="text-sm text-[#5C625E] font-medium"
                >
                  ≈ {formatKcal(item.calories * qty)}
                  {qty > 1 && (
                    <span className="text-xs ml-1">
                      ({qty} × {item.calories})
                    </span>
                  )}
                </p>
              )}
              <button
                type="button"
                data-testid="dialog-nutrition-link"
                onClick={() => setNutritionOpen(true)}
                className="inline-flex items-center gap-1 text-xs text-[#5A7247] hover:text-[#46583a] font-medium underline decoration-dotted underline-offset-2"
              >
                <Info className="h-3 w-3" strokeWidth={2} />
                Nutrition info
              </button>
            </div>
          </div>


          {item.long_description && (
            <div
              data-testid="addon-long-description"
              className="mt-4 p-4 bg-[#F3F0EA] rounded-xl text-[#5C625E] leading-relaxed text-sm whitespace-pre-line"
            >
              {item.long_description}
            </div>
          )}

          {madeWith.length > 0 && (
            <div
              data-testid="addon-made-with"
              className="mt-4 p-4 rounded-xl border border-[#E8E5DF] bg-white"
            >
              <div className="flex items-center gap-2 mb-2">
                <Leaf className="h-4 w-4 text-[#5A7247]" strokeWidth={1.75} />
                <p className="text-[11px] uppercase tracking-[0.15em] font-bold text-[#5A7247]">
                  Made with
                </p>
              </div>
              <p className="text-sm text-[#5C625E] leading-relaxed">
                {madeWith.map((m) => m.name).join(" · ")}
              </p>
            </div>
          )}

          {priceCols.length > 1 && (
            <div className="mt-5" data-testid="addon-size-row">
              <p className="text-xs uppercase tracking-wider text-[#5C625E] font-semibold mb-2">
                Size
              </p>
              <div className="flex flex-wrap gap-2">
                {priceCols.map((c) => {
                  const v = (item.prices || {})[c.key];
                  if (v == null || v === "" || Number(v) <= 0) return null;
                  const checked = priceColumn === c.key;
                  return (
                    <button
                      key={c.key}
                      type="button"
                      data-testid={`addon-size-${c.key}`}
                      onClick={() => setPriceColumn(c.key)}
                      className={`px-4 h-10 rounded-full text-sm border transition-colors ${
                        checked
                          ? "bg-[#C05746] border-[#C05746] text-white"
                          : "bg-white border-[#E8E5DF] text-[#121614] hover:border-[#C05746]"
                      }`}
                    >
                      <span className="font-semibold tracking-wide mr-2">{c.label || c.key.toUpperCase()}</span>
                      <span className="opacity-80">
                        {isWhole ? `$${Math.round(Number(v))}` : `$${Number(v).toFixed(2)}`}
                      </span>
                    </button>
                  );
                })}
              </div>
            </div>
          )}

          <div className="mt-6 space-y-6">
            {(item.addons || []).map((group, i) => (
              <div key={i} data-testid={`addon-group-${i}`}>
                <div className="flex items-baseline justify-between mb-3">
                  <p className="font-medium text-[#121614]">
                    {group.name}{" "}
                    {group.required && (
                      <span className="text-xs text-[#C05746] ml-1">Required</span>
                    )}
                  </p>
                  <p className="text-xs uppercase tracking-wider text-[#5C625E]">
                    {group.type === "single" ? "Choose one" : "Choose any"}
                  </p>
                </div>
                {group.type === "single" ? (
                  <RadioGroup
                    value={selections[i] || ""}
                    onValueChange={(v) => setSingle(i, v)}
                    className="space-y-2"
                  >
                    {(group.options || []).map((opt) => {
                      // ── Serving-style groups (Glass/Bottle for wines, sometimes
                      // pot/cup for tea) display ABSOLUTE totals rather than a
                      // delta — otherwise selecting "Bottle +$38" on top of a $22
                      // glass reads as confusing addition rather than the $60
                      // bottle price the customer is paying.
                      const groupName = (group.name || "").toLowerCase();
                      const isAbsoluteGroup = groupName === "serving" || groupName === "size";
                      const absolute = unitPrice + (Number(opt.price) || 0);
                      return (
                        <label
                          key={opt.name}
                          data-testid={`addon-option-${i}-${opt.name.replace(/\s+/g, "-")}`}
                          className="flex items-center justify-between px-4 py-3 rounded-xl border border-[#E8E5DF] hover:bg-[#F3F0EA] cursor-pointer transition-colors"
                        >
                          <div className="flex items-center gap-3">
                            <RadioGroupItem value={opt.name} id={`opt-${i}-${opt.name}`} />
                            <span className="text-sm">{opt.name}</span>
                          </div>
                          {isAbsoluteGroup ? (
                            <span className="text-sm font-medium text-[#121614] tabular-nums">
                              {money(absolute)}
                            </span>
                          ) : opt.price > 0 ? (
                            <span className="text-sm text-[#C05746]">+{money(opt.price)}</span>
                          ) : null}
                        </label>
                      );
                    })}
                  </RadioGroup>
                ) : (
                  <div className="space-y-2">
                    {(group.options || []).map((opt) => {
                      const checked = (selections[i] || []).includes(opt.name);
                      return (
                        <label
                          key={opt.name}
                          data-testid={`addon-option-${i}-${opt.name.replace(/\s+/g, "-")}`}
                          className="flex items-center justify-between px-4 py-3 rounded-xl border border-[#E8E5DF] hover:bg-[#F3F0EA] cursor-pointer transition-colors"
                        >
                          <div className="flex items-center gap-3">
                            <Checkbox
                              checked={checked}
                              onCheckedChange={() => toggleMulti(i, opt.name)}
                            />
                            <span className="text-sm">{opt.name}</span>
                          </div>
                          {opt.price > 0 && (
                            <span className="text-sm text-[#C05746]">+{money(opt.price)}</span>
                          )}
                        </label>
                      );
                    })}
                  </div>
                )}
              </div>
            ))}

            <div>
              <Label className="font-medium" htmlFor="addon-notes">
                Special notes
              </Label>
              <Textarea
                id="addon-notes"
                data-testid="addon-notes-input"
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                placeholder="Allergies, preferences, anything we should know…"
                className="mt-2 rounded-xl"
              />
            </div>

            <div className="flex items-center justify-between border-t border-[#E8E5DF] pt-4">
              <div className="flex items-center gap-3">
                <button
                  type="button"
                  data-testid="addon-qty-decrement"
                  onClick={() => setQty((q) => Math.max(1, q - 1))}
                  className="h-10 w-10 rounded-full border border-[#E8E5DF] flex items-center justify-center hover:bg-[#F3F0EA]"
                  aria-label="Decrease quantity"
                >
                  <Minus className="h-4 w-4" strokeWidth={1.5} />
                </button>
                <span data-testid="addon-qty" className="text-lg font-medium w-6 text-center">
                  {qty}
                </span>
                <button
                  type="button"
                  data-testid="addon-qty-increment"
                  onClick={() => setQty((q) => q + 1)}
                  className="h-10 w-10 rounded-full border border-[#E8E5DF] flex items-center justify-center hover:bg-[#F3F0EA]"
                  aria-label="Increase quantity"
                >
                  <Plus className="h-4 w-4" strokeWidth={1.5} />
                </button>
              </div>
              <p className="text-2xl font-serif-display" data-testid="addon-total">
                {money(lineTotalPreview)}
              </p>
            </div>
          </div>

          <DialogFooter className="mt-6 sm:flex-row gap-2">
            <Button
              type="button"
              variant="outline"
              data-testid="addon-cancel-button"
              onClick={onClose}
              className="rounded-full px-8 h-12 border-[#121614] text-[#121614] hover:bg-[#121614] hover:text-white sm:flex-1"
            >
              Cancel
            </Button>
            <Button
              type="button"
              data-testid="addon-add-to-cart"
              onClick={handleAdd}
              className="bg-[#C05746] hover:bg-[#A64A3C] text-white rounded-full px-8 h-12 sm:flex-1"
            >
              Add to Order — {money(lineTotalPreview)}
            </Button>
          </DialogFooter>
        </div>
      </DialogContent>
      <NutritionModal
        item={nutritionOpen ? item : null}
        onClose={() => setNutritionOpen(false)}
      />
    </Dialog>
  );
}
